import { useEffect, useMemo, useState } from "react";
import { Search, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Language picker with search. Accepts value as ISO code (e.g. "en-US", "fr").
 * onChange(langObject) with the picked entry from the catalog.
 */
export default function LanguagePicker({
    value,
    onChange,
    testIdPrefix = "lang",
    placeholder = "Rechercher une langue…",
    excludeIsos = [],
}) {
    const [langs, setLangs] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/languages")
            .then((r) => setLangs(r.data.languages || []))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const excl = new Set(excludeIsos);
        return langs.filter((l) => {
            if (excl.has(l.iso)) return false;
            if (!q) return true;
            return (
                l.native.toLowerCase().includes(q) ||
                l.fr.toLowerCase().includes(q) ||
                l.iso.toLowerCase().includes(q)
            );
        });
    }, [langs, query, excludeIsos]);

    return (
        <div className="space-y-3" data-testid={`${testIdPrefix}-picker`}>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-inkSoft" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={placeholder}
                    data-testid={`${testIdPrefix}-search`}
                    className="w-full h-11 pl-10 pr-4 rounded-xl border border-lexiBorder bg-paper focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
            </div>

            <div
                className="max-h-72 overflow-y-auto rounded-xl border border-lexiBorder bg-white divide-y divide-lexiBorder"
                data-testid={`${testIdPrefix}-list`}
            >
                {loading && (
                    <div className="p-4 text-sm text-inkSoft" data-testid={`${testIdPrefix}-loading`}>
                        Chargement…
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="p-4 text-sm text-inkSoft" data-testid={`${testIdPrefix}-empty`}>
                        Aucun résultat.
                    </div>
                )}
                {filtered.map((l) => {
                    const active = l.iso === value;
                    return (
                        <button
                            key={l.iso}
                            type="button"
                            onClick={() => onChange(l)}
                            data-testid={`${testIdPrefix}-opt-${l.iso}`}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors ${
                                active ? "bg-emerald text-white" : "hover:bg-paperAlt"
                            }`}
                        >
                            <span className="flex items-center gap-3 min-w-0">
                                <span className="text-lg leading-none shrink-0">{l.flag}</span>
                                <span className="flex flex-col min-w-0">
                                    <span className="font-serif text-base leading-none truncate">
                                        {l.native}
                                    </span>
                                    <span className={`text-xs mt-0.5 ${active ? "text-white/80" : "text-inkSoft"}`}>
                                        {l.fr}
                                    </span>
                                </span>
                            </span>
                            <span className="flex items-center gap-2 shrink-0">
                                <QualityBadge quality={l.tts} inverted={active} />
                                {active && <Check className="w-4 h-4 text-white" />}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export function QualityBadge({ quality, inverted }) {
    if (quality === "excellent") return null;
    const cls =
        quality === "bon"
            ? inverted
                ? "border-white/40 text-white/90"
                : "border-lexiBorder text-inkSoft"
            : inverted
              ? "border-white/50 text-white bg-white/10"
              : "border-terracotta/30 text-terracotta bg-terracotta/8";
    const label = quality === "bon" ? "audio bon" : "audio limité";
    return (
        <span className={`text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5 rounded-full border ${cls}`}>
            {label}
        </span>
    );
}
